import React, {
  useContext,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
} from 'react';
import { AppContext } from '../context';
import env from '../config/environment';
import Section from '../components/Section';
import PageContent from '../components/PageContent';
import { FlatButton } from '../components/Button';
import ProductSummaryBanner from '../components/ProductSummaryBanner';
import { useSaveCotation, useProposal, useLgpdConsent } from '../hooks';
import GeneralConditions from '../components/GeneralConditions';
import ClientIdentification from '../components/ClientIdentification';
import ConfirmPersonalData from '../components/ConfirmPersonalData';
import ConfirmAddressData from '../components/ConfirmAddressData';
import ConfirmContactData from '../components/ConfirmContactData';
import { confirmDataTitle, confirmDataSubtitle } from '../utils/constants';
import { findContact } from '../utils/utils';

const InfoTypeValue = userData => {
  const contactWay = userData.pessoa.comunicacao.recebimento[0];
  const allFalse = Object.values(contactWay).every(
    i => contactWay[i] === false
  );

  return contactWay.celular && contactWay.email
    ? `Celular e Email`
    : (contactWay.celular && `Celular`) ||
        (contactWay.email && `Email`) ||
        (allFalse && `-`);
};

const ConfirmData = ({ history }) => {
  const {
    userData,
    productData,
    isMobile,
    setContext,
    ibcLogged,
    clientAccs,
    lgpdConsent,
  } = useContext(AppContext);
  const [agreed, setAgreed] = useState(false);
  const {
    saveCotation,
    loading: cotationLoading,
    cotation,
  } = useSaveCotation();
  const { getProposal, loading: proposalLoading, proposal } = useProposal();
  const { loading: loadingLgpd, postLgpdConsent } = useLgpdConsent();
  const submit = async () => {
    const agencyNumber = clientAccs && clientAccs[0]?.agency;
    await saveCotation(productData, userData, ibcLogged, agencyNumber);
  };

  const sendLgpdConsent = useCallback(async () => {
    if (proposal.clientes) {
      const cpf = proposal.clientes[0].pessoa.identificacao.codigo;
      const promises = [
        postLgpdConsent(
          env.BUSINESS_PROCESS_CODE,
          cpf,
          proposal.identificacao.codigoNegocio,
          lgpdConsent,
          env.BUSSINESS_SUBPROCESS_CAIXA_HOLDING_CODE
        ),
        postLgpdConsent(
          env.BUSINESS_PROCESS_CODE,
          cpf,
          proposal.identificacao.codigoNegocio,
          lgpdConsent,
          env.BUSSINESS_SUBPROCESS_CAIXA_HOLDING_PARTNERS_CODE
        ),
      ];
      Promise.all(promises).then(res => {
        if (res.every(el => el.mensagem)) {
          setContext({
            proposal,
            navigateTo: '/pagamento',
          });
        }
      });
    }
  }, [lgpdConsent, proposal, setContext, postLgpdConsent]);

  useEffect(() => {
    if (cotation?.[0]?.identificacao) {
      getProposal(parseInt(cotation[0].identificacao.codigo, 10));
    }
  }, [cotation, getProposal]);

  useEffect(() => {
    sendLgpdConsent();
  }, [proposal, setContext, sendLgpdConsent]);

  useLayoutEffect(() => {
    setContext({
      loading: proposalLoading || cotationLoading || loadingLgpd,
    });
  }, [proposalLoading, cotationLoading, loadingLgpd, setContext]);

  return (
    <PageContent className="confirmData" step={isMobile ? 5 : 3}>
      <ProductSummaryBanner
        title={confirmDataTitle}
        subtitle={confirmDataSubtitle}
        data={productData.itemCapitalizacao.titulos[0].planosTitulos}
      />
      <ClientIdentification
        clientType={
          userData.pessoa.identificacao.tipo === 'F' ? 'Física' : 'Jurídica'
        }
      />

      <ConfirmPersonalData
        clientName={userData.pessoa.nome}
        clientSex={
          userData.pessoa.dadosPessoaFisica.sexo.codigo === 'M'
            ? 'Masculino'
            : 'Feminino'
        }
        clientCPF={userData.pessoa.identificacao.codigo}
        clientBirthdate={userData.pessoa.dadosPessoaFisica.dataNascimento}
        clientMaritalState={userData.pessoa.dadosPessoaFisica.estadoCivil.valor}
        clientProfession={userData.pessoa.dadosPessoaFisica.profissao.valor}
        clientIndividualIncome={
          userData.pessoa.informacoesEconomicas.faixaRenda[1]
        }
        clientFamilyIncome={userData.pessoa.informacoesEconomicas.faixaRenda[0]}
      />

      <ConfirmAddressData
        clientCEP={userData.pessoa.endereco.cep}
        clientAddress={userData.pessoa.endereco.logradouro}
        clientNumber={userData.pessoa.endereco.numero}
        clientAddition={userData.pessoa.endereco.complemento}
        clientNeighborhood={userData.pessoa.endereco.bairro}
        clientCity={userData.pessoa.endereco.cidade}
        clientState={userData.pessoa.endereco.estado}
      />

      <ConfirmContactData
        clientCellphone={findContact(
          'CEL',
          userData.pessoa.comunicacao.parametros
        )}
        clientResidencePhone={findContact(
          'RES',
          userData.pessoa.comunicacao.parametros
        )}
        clientComercialPhone={findContact(
          'COM',
          userData.pessoa.comunicacao.parametros
        )}
        clientEmail={findContact('PES', userData.pessoa.comunicacao.parametros)}
        clientInformationType={InfoTypeValue(userData)}
      />

      <Section title="Condições gerais">
        <GeneralConditions
          checked={agreed}
          onChangeBtn={() => setAgreed(!agreed)}
          text="Li o regulamento e estou ciente das regras do plano."
          productCode={productData.oferta.identificacao.codigoProduto}
        />
      </Section>
      <Section flexEnd noPadding bgTransparent inlineChildren>
        <FlatButton
          column
          hasArrow
          arrowDirection="left"
          arrowSize={5}
          back
          onClick={() => {
            history.goBack();
          }}
          margin
        >
          Voltar
        </FlatButton>
        <FlatButton
          column
          hasArrow
          inverse
          arrowDirection="right"
          arrowSize={5}
          disabled={!agreed}
          onClick={submit}
        >
          Continuar
        </FlatButton>
      </Section>
    </PageContent>
  );
};
export default ConfirmData;
